// Покадровый trace CV-модуля → данные для графиков /demo/full-metrics.
//
// Trace — колонки, а не строки: {frame: [...], t_ms: [...], rep_id: [...],
// <значение>: [...]} (lgp_cv_module docs/client_report_mode.md, «Per-frame
// trace»). Тип колонки определяется по первому не-null значению:
// число → график, boolean → полоса «проверка пройдена/нет», строка → полоса
// категорий (feedback). null — кадр без значения: разрыв линии, не ноль.

// Больше точек SVG-график recharts не тянет: длинный trace прореживается.
export const MAX_POINTS = 2000;
export const TRACE_VERSION = 1;

const BOOKKEEPING = ['frame', 't_ms', 'rep_id'];

// Значение рядом со своим порогом. Нет колонок — нет графика. Группа из одних
// порогов — то, что задано настройками, а не измерено (рисуется ступенькой).
const GROUPS = [
  { title: 'Растяжение губ', values: ['stretch_left', 'stretch_right'], thresholds: ['stretch_needed'] },
  // открытие рта: «Заборчик», «Открой рот» (нужно и минимум) и «Улыбка» (максимум: рот закрыт)
  { title: 'Открытие рта', values: ['mouth_open'], thresholds: ['mouth_open_needed', 'mouth_open_min', 'mouth_open_max'] },
  { title: 'Видимость зубов', values: ['teeth_lip_area_ratio'], thresholds: ['teeth_lip_area_ratio_min'] },
  { title: 'Сжатие зубов', values: ['teeth_gap_ratio'], thresholds: ['teeth_gap_ratio_max'] },
  // «Открой рот»
  { title: 'Рот не слишком широкий', values: ['mouth_stretch'], thresholds: ['mouth_stretch_max'] },
  // «Улыбка»
  { title: 'Угол улыбки', values: ['smile_angle'], thresholds: ['smile_angle_needed'] },
  // левая и правая части (до основания носа) в сумме дают ширину; сами не проверяются
  { title: 'Ширина рта', values: ['mouth_width', 'mouth_width_left', 'mouth_width_right'], thresholds: ['mouth_width_needed'] },
  // уголки над линией через середину верхней губы, параллельной глазам; «улыбка» требует обе > 0
  { title: 'Высота уголков губ', values: ['corner_height_left', 'corner_height_right'], thresholds: ['corner_height_min'] },
  // не округлость: высота губ снаружи к ширине. «Улыбка»: выше максимума — подсказка «улыбнись»;
  // «Поцелуйчик»: нужно выше минимума (губы вытянуты трубочкой)
  // у «Поцелуйчика» ещё левая и правая половины (высота губы на своей стороне к своей половине ширины)
  { title: 'Высота рта к ширине', values: ['mouth_round', 'mouth_round_left', 'mouth_round_right'],
    thresholds: ['mouth_round_max', 'mouth_round_min'] },
  // «Поцелуйчик»: щель между масками губ к длине носа; null — маски губ не найдены
  { title: 'Щель между губами', values: ['lips_gap'], thresholds: ['lips_gap_max', 'lips_gap_open_max'] },
  // «Удивись»
  { title: 'Брови', values: ['brow_left', 'brow_right'], thresholds: ['brows_needed'] },
  { title: 'Глаза открыты', values: ['eye_left', 'eye_right'], thresholds: ['eyes_open_min'] },
  { title: 'Пороги персонализации', values: [], thresholds: ['open_lips_thresh', 'mouth_stretch_threshold'] },
  // hold_done_ms — сколько уже удержано (растёт от 0 до hold_ms, при сбое позы — снова 0).
  // В старых trace его нет: тогда рисуется один порог.
  { title: 'Удержание', values: ['hold_done_ms'], thresholds: ['hold_ms'], thresholdsAlone: true },
];

const THRESHOLDS = new Set(GROUPS.flatMap((g) => g.thresholds));

// Состояния, а не проверки: «нет» здесь не ошибка.
const STATES = new Set(['repetition_in_progress', 'teeth_or_tongue_visible', 'face_still', 'lips_found']);

export const LABELS = {
  stretch_left: 'левый угол',
  stretch_right: 'правый угол',
  stretch_needed: 'нужно',
  mouth_open: 'открытие',
  mouth_open_needed: 'нужно',
  teeth_lip_area_ratio: 'зубы / губы',
  teeth_lip_area_ratio_min: 'минимум',
  teeth_gap_ratio: 'щель между зубами',
  teeth_gap_ratio_max: 'максимум',
  hold_ms: 'нужно, мс',
  hold_done_ms: 'удержано, мс',
  open_lips_thresh: 'порог открытия',
  mouth_stretch_threshold: 'порог растяжения',
  feedback: 'подсказка',
  repetition_in_progress: 'идёт повтор',
  tongue_inside_ok: 'язык во рту',
  teeth_clenched_ok: 'зубы сжаты',
  mouth_stretched_ok: 'губы растянуты',
  mouth_open_ok: 'рот открыт',
  teeth_visible_ok: 'зубы видны',
  mouth_open_min: 'минимум',
  mouth_open_max: 'максимум (рот закрыт)',
  mouth_stretch: 'ширина к калибровке',
  mouth_stretch_max: 'максимум',
  mouth_not_too_wide_ok: 'рот не слишком широкий',
  teeth_or_tongue_visible: 'видны зубы или язык',
  mouth_closed_ok: 'рот закрыт',
  mouth_round: 'высота / ширина',
  mouth_round_max: 'максимум',
  mouth_round_ok: 'рот не слишком высокий',
  mouth_round_min: 'минимум',
  mouth_round_left: 'левая половина',
  mouth_round_right: 'правая половина',
  mouth_round_enough_ok: 'губы вытянуты',
  face_still: 'нет движения',
  lips_found: 'губы найдены',
  lips_gap: 'щель / нос',
  lips_gap_max: 'губы сомкнуты ниже',
  lips_gap_open_max: 'рот открыт выше',
  lips_closed_ok: 'губы сомкнуты',
  mouth_not_open_ok: 'рот не открыт',
  smile_angle: 'угол уголков, °',
  smile_angle_needed: 'нужно',
  smile_ok: 'улыбка',
  mouth_width: 'ширина рта',
  mouth_width_needed: 'нужно',
  mouth_width_left: 'левая часть',
  mouth_width_right: 'правая часть',
  corner_height_left: 'левый уголок',
  corner_height_right: 'правый уголок',
  corner_height_min: 'минимум',
  brow_left: 'левая бровь',
  brow_right: 'правая бровь',
  brows_needed: 'нужно',
  brow_left_ok: 'левая бровь поднята',
  brow_right_ok: 'правая бровь поднята',
  eye_left: 'левый глаз',
  eye_right: 'правый глаз',
  eyes_open_min: 'минимум',
  eyes_open_ok: 'глаза открыты',
};

export const label = (name) => LABELS[name] || name;

const seconds = (ms) => Math.round(ms) / 1000;

/**
 * null, если trace можно рисовать; иначе — почему нельзя. /server принимает
 * любой JSON-объект, а страница не должна падать на чужой версии или ошибке модуля.
 */
export function traceProblem(trace) {
  if (!trace || trace.trace_version !== TRACE_VERSION) return 'Формат trace не поддерживается';
  const columns = trace.columns;
  if (!columns || typeof columns !== 'object') return 'В trace нет колонок';
  const n = columns.t_ms?.length;
  for (const name of Object.keys(columns)) {
    if (!Array.isArray(columns[name]) || columns[name].length !== n) return `Колонка «${name}» повреждена`;
  }
  if (!BOOKKEEPING.every((name) => Array.isArray(columns[name]))) return 'В trace нет времени кадров';
  return null;
}

function kindOf(values) {
  const first = values.find((v) => v !== null && v !== undefined);
  if (typeof first === 'number') return 'number';
  if (typeof first === 'boolean') return 'check';
  if (typeof first === 'string') return 'category';
  return null;
}

const namesOf = (columns, kind) =>
  Object.keys(columns).filter((name) => !BOOKKEEPING.includes(name) && kindOf(columns[name]) === kind);

function extreme(values, from, to, pick) {
  let best = null;
  for (let i = from; i < to; i += 1) {
    const v = values[i];
    if (v !== null && v !== undefined && (best === null || pick(v, best))) best = v;
  }
  return best;
}

/**
 * Строки для recharts: {t (с), frame, rep_id, <число>…}.
 * До MAX_POINTS кадров — все. Дальше кадры режутся на корзины, и у каждой две
 * строки: минимум измеренных значений в начале корзины и максимум в конце, так
 * что короткий всплеск или провал не теряется. Пороги — как есть на этих
 * кадрах, плюс строка на каждом кадре, где порог меняется: ступенька не опаздывает.
 */
export function chartRows(columns) {
  const n = columns.t_ms.length;
  const numeric = namesOf(columns, 'number');
  const measured = numeric.filter((name) => !THRESHOLDS.has(name));
  const thresholds = numeric.filter((name) => THRESHOLDS.has(name));
  const frameRow = (i) => {
    const row = { t: seconds(columns.t_ms[i]), frame: columns.frame[i], rep_id: columns.rep_id[i] };
    for (const name of numeric) row[name] = columns[name][i] ?? null;
    return row;
  };
  if (n <= MAX_POINTS) return { rows: [...Array(n).keys()].map(frameRow), step: 1 };

  const step = Math.ceil((n * 2) / MAX_POINTS);
  const rows = [];
  for (let start = 0; start < n; start += step) {
    const end = Math.min(start + step, n);
    const low = frameRow(start);
    const high = frameRow(end - 1);
    for (const name of measured) {
      low[name] = extreme(columns[name], start, end, (v, best) => v < best);
      high[name] = extreme(columns[name], start, end, (v, best) => v > best);
    }
    rows.push(low);
    for (let i = start + 1; i < end - 1; i += 1) {
      if (thresholds.some((name) => columns[name][i] !== columns[name][i - 1])) rows.push(frameRow(i));
    }
    if (end - 1 > start) rows.push(high);
  }
  return { rows, step };
}

/** Графики: известные группы «значение + порог», затем каждое незнакомое число отдельно. */
export function chartGroups(columns) {
  const numeric = new Set(namesOf(columns, 'number'));
  const groups = [];
  for (const g of GROUPS) {
    const values = g.values.filter((name) => numeric.has(name));
    const thresholds = g.thresholds.filter((name) => numeric.has(name));
    if (values.length === 0 && thresholds.length === 0) continue;
    if (values.length === 0 && g.values.length > 0 && !g.thresholdsAlone) continue; // порог без значения не о чем рисовать
    groups.push({ title: g.title, values, thresholds });
    [...g.values, ...g.thresholds].forEach((name) => numeric.delete(name));
  }
  for (const name of numeric) groups.push({ title: name, values: [name], thresholds: [] });
  return groups;
}

/** Полосы: категории, состояния и проверки — отрезки одинаковых значений, по всем кадрам. */
export function lanes(columns) {
  const t = columns.t_ms.map(seconds);
  const make = (name, kind) => {
    const values = columns[name];
    const runs = [];
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i] ?? null;
      const last = runs[runs.length - 1];
      if (last && last.value === value) continue;
      if (last) last.to = t[i];
      runs.push({ from: t[i], to: t[i], value });
    }
    if (runs.length) runs[runs.length - 1].to = t[t.length - 1];
    return { name, kind, runs };
  };
  const booleans = namesOf(columns, 'check');
  return [
    ...namesOf(columns, 'category').map((name) => make(name, 'category')),
    ...booleans.filter((name) => STATES.has(name)).map((name) => make(name, 'state')),
    ...booleans.filter((name) => !STATES.has(name)).map((name) => make(name, 'check')),
  ];
}

/** Начала повторов: кадры, где меняется rep_id. */
export function repStarts(columns) {
  const starts = [];
  for (let i = 1; i < columns.rep_id.length; i += 1) {
    if (columns.rep_id[i] !== columns.rep_id[i - 1]) starts.push({ t: seconds(columns.t_ms[i]), repId: columns.rep_id[i] });
  }
  return starts;
}
